import { useInfiniteQuery, useQuery } from 'react-query'
import axios from 'lib/axiosConfig'
import { FeeProduct } from 'types/api/product'
import { useEffect } from 'react'
import { contentLanguages } from 'lib/languages'
import i18n from 'lib/i18'

export const useGetFeeProducts = (placeId: string) => {
  const {
    isLoading,
    error,
    data: response,
  } = useQuery(
    ['getFeeProducts'],
    async () =>
      axios.get(`/v1/places/${placeId}/extra-products`, {
        params: { type: 'FEE' },
      }),
    {
      keepPreviousData: true,
    }
  )

  const feeProducts =
    response?.data?.map((product: FeeProduct) => {
      return {
        masterProductId: product.masterProductId,
        name: product.product?.name || '',
      }
    }) || []
  return { isLoading, error, feeProducts }
}

export const useGetMasterProducts = (
  page: number,
  pageSize: number,
  query?: string,
  queryType?: string,
  enabledCondition?: boolean
) => {
  const productsPayload: { [key: string | symbol]: string | number } = {
    page,
    page_size: pageSize,
    data: 'full',
  }
  if (query && queryType) {
    productsPayload[queryType] = query
  }
  const queryKey = page + (query || '') + (queryType || '')
  const {
    isFetching,
    error,
    data: response,
  } = useQuery(
    ['getMasterProducts', queryKey],
    async () =>
      axios.get(`/v2/master-products`, {
        params: productsPayload,
      }),
    {
      ...(enabledCondition && { enabled: Boolean(query && query?.length > 0) }),
    }
  )

  const pagination = response?.data.meta.pagination
  const products = response?.data.data || []

  return { isFetching, error, products, pagination }
}

export const useGetInfiniteCategory = (name: string) => {
  const { data, hasNextPage, isFetchingNextPage, isFetching, fetchNextPage } =
    useInfiniteQuery(
      ['allCategoryNames', name],
      async ({ pageParam = 1 }) => {
        const { data } = await axios.get(`/v1/category-trees/${name}`, {
          params: {
            page: pageParam,
            page_size: 5000,
          },
        })
        const categoryData = data.data.category
        return {
          meta: data.meta,
          categories: categoryData,
        }
      },
      {
        enabled: Boolean(name),
        getNextPageParam: (lastPage) => lastPage.meta.pagination.nextPage,
      }
    )

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && name) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage])

  const allPages = data?.pages.flatMap((data) => data.categories) || []

  const categoryData =
    allPages && allPages?.length > 0
      ? allPages.flatMap((category) => {
          const categoryName = category.name.find(
            (n: { $: string; '@languageCode': string }) =>
              n['@languageCode'] === i18n.language ||
              n['@languageCode'] === contentLanguages[0]
          )?.$
          if (!categoryName) return []
          return { label: categoryName, value: category.extId }
        })
      : []

  return { isFetching, categoryData }
}
